import { z } from 'zod';

import { requestJson } from '@/lib/api/requests';

const ResolutionTierSchema = z.enum([
  'local_guard',
  'cache_exact',
  'cache_semantic',
  'fast_model',
  'premium_model',
]);

const PublicAssistantMessageSchema = z.object({
  id: z.string(),
  role: z.enum(['assistant', 'guest']),
  content: z.string(),
  createdAt: z.string(),
});

const PublicAssistantActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('add_to_cart'),
    itemId: z.string(),
    quantity: z.number().default(1),
    modifierIds: z.array(z.string()).default([]),
    notes: z.string().nullable().optional().default(null),
  }),
  z.object({
    type: z.literal('show_allergens'),
    itemId: z.string().nullable(),
    allergenNames: z.array(z.string()).default([]),
  }),
]);

const PublicAssistantSafetySchema = z.object({
  flags: z.array(z.enum(['profanity', 'allergen'])).default([]),
  reasons: z.array(z.enum(['low_confidence', 'repeated_clarification'])).default([]),
  confidence: z.number().nullable(),
  handoff: z.boolean(),
  disclaimers: z.array(z.string()).default([]),
});

const PublicAssistantQuotaSchema = z.object({
  status: z.enum(['disabled', 'ok', 'warning', 'critical', 'exhausted', 'unlimited']),
  remainingChats: z.number().nullable(),
  shouldBackoff: z.boolean(),
  message: z.string().nullable(),
});

const PublicAssistantUsageSchema = z.object({
  tokensIn: z.number().nullable(),
  tokensOut: z.number().nullable(),
});

const PublicAssistantResponseSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    conversationId: z.string().uuid(),
    response: z.string(),
    messages: z.array(PublicAssistantMessageSchema),
    quota: PublicAssistantQuotaSchema,
    usage: PublicAssistantUsageSchema,
    actions: z.array(PublicAssistantActionSchema).default([]),
    safety: PublicAssistantSafetySchema.optional(),
    resolutionTier: ResolutionTierSchema.optional(),
    router: z
      .object({
        tier: ResolutionTierSchema,
        applied: z.enum(['active', 'shadow', 'disabled', 'holdout']),
        reason: z.string(),
      })
      .optional(),
  }),
});

export type PublicAssistantMessage = z.infer<typeof PublicAssistantMessageSchema>;
export type PublicAssistantAction = z.infer<typeof PublicAssistantActionSchema>;
export type PublicAssistantSafety = z.infer<typeof PublicAssistantSafetySchema>;
export type ResolutionTier = z.infer<typeof ResolutionTierSchema>;

export type PublicAssistantResponse = z.infer<typeof PublicAssistantResponseSchema>['data'];

const GuardLocaleResponseSchema = z.object({
  phrases: z.array(z.string()),
  response: z.string(),
  badgeLabel: z.string().optional(),
});

const GuardRuleSchema = z.object({
  id: z.string(),
  category: z.enum(['faq', 'policy', 'allergen']),
  priority: z.number(),
  tags: z.array(z.string()),
  requiresPremiumFallback: z.boolean().optional(),
  locales: z.object({
    en: GuardLocaleResponseSchema.optional(),
    es: GuardLocaleResponseSchema.optional(),
  }),
});

const GuardLibrarySchema = z.object({
  version: z.string(),
  disclaimers: z.record(
    z.object({
      en: z.string(),
      es: z.string(),
    }),
  ),
  rules: z.array(GuardRuleSchema),
});

const AssistantFeatureConfigSchema = z.object({
  instantGuard: z.object({
    enabled: z.boolean(),
    shadowMode: z.boolean(),
    version: z.string(),
  }),
  semanticCache: z.object({
    enabled: z.boolean(),
    shadowMode: z.boolean(),
    ttlSeconds: z.number(),
    maxEntries: z.number(),
  }),
  tierRouter: z.object({
    enabled: z.boolean(),
    shadowMode: z.boolean(),
    fastModel: z.string(),
    premiumModel: z.string(),
    sensitiveTags: z.array(z.string()),
    perTenantOverrides: z.record(
      z.object({
        forcedTier: z.enum(['fast', 'premium']).optional(),
        sensitiveIntents: z.array(z.string()).optional(),
      }),
    ),
  }),
  guardLibrary: GuardLibrarySchema,
});

const AssistantFeatureConfigResponseSchema = z.object({
  ok: z.literal(true),
  data: AssistantFeatureConfigSchema,
});

export type PublicAssistantFeatureConfig = z.infer<typeof AssistantFeatureConfigSchema>;

export type SendPublicAssistantMessageParams = {
  tenantSlug: string;
  tableSlug: string;
  token: string;
  message: string;
  guestToken?: string;
  guestName?: string;
  guestPhone?: string;
  conversationId?: string;
  locale?: string;
  previewOnly?: boolean;
  signal?: AbortSignal;
};

const buildHeaders = (tenantSlug: string, token: string): Record<string, string> => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'X-Tenant': tenantSlug,
  'X-QR-Token': token,
});

export async function sendPublicAssistantMessage(
  params: SendPublicAssistantMessageParams,
): Promise<PublicAssistantResponse> {
  const {
    tenantSlug,
    tableSlug,
    token,
    message,
    conversationId,
    locale,
    previewOnly,
    guestToken,
    guestName,
    guestPhone,
    signal,
  } = params;

  if (!tenantSlug || !tableSlug || !token) {
    throw new Error('tenantSlug, tableSlug, and token are required to contact the assistant.');
  }

  const search = new URLSearchParams({ token });
  const path = `/public/table/${encodeURIComponent(tableSlug)}/assistant?${search.toString()}`;

  const body: Record<string, unknown> = {
    message,
  };

  if (conversationId) {
    body.conversationId = conversationId;
  }
  if (locale) {
    body.locale = locale;
  }
  if (typeof previewOnly === 'boolean') {
    body.previewOnly = previewOnly;
  }
  if (guestToken) {
    body.guestToken = guestToken;
  }
  if (guestName) {
    body.guestName = guestName;
  }
  if (guestPhone) {
    body.guestPhone = guestPhone;
  }

  const headers = buildHeaders(tenantSlug, token);
  if (guestToken) {
    headers['X-Guest-Token'] = guestToken;
  }
  if (guestPhone) {
    headers['X-Guest-Phone'] = guestPhone;
  }

  const response = await requestJson<z.infer<typeof PublicAssistantResponseSchema>>(path, {
    method: 'POST',
    body,
    headers,
    schema: PublicAssistantResponseSchema,
    signal,
  });

  return response.data;
}

type FetchAssistantConfigParams = {
  tenantSlug: string;
  token?: string;
  signal?: AbortSignal;
};

export async function fetchAssistantFeatureConfig(
  params: FetchAssistantConfigParams,
): Promise<PublicAssistantFeatureConfig> {
  const { tenantSlug, token, signal } = params;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Tenant': tenantSlug,
  };
  if (token) {
    headers['X-QR-Token'] = token;
  }
  const response = await requestJson<z.infer<typeof AssistantFeatureConfigResponseSchema>>(
    '/public/table/config',
    {
      method: 'GET',
      headers,
      schema: AssistantFeatureConfigResponseSchema,
      signal,
    },
  );
  return response.data;
}
